import { __decorate } from "tslib";
import React, { cloneElement, isValidElement } from 'react';
import { action, computed, isArrayLike, observable, runInAction, toJS } from 'mobx';
import classNames from 'classnames';
import isPromise from 'is-promise';
import isNumber from 'lodash/isNumber';
import isNil from 'lodash/isNil';
import isLdEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import defaultTo from 'lodash/defaultTo';
import uniqWith from 'lodash/uniqWith';
import isFunction from 'lodash/isFunction';
import findLastIndex from 'lodash/findLastIndex';
import { observer } from 'mobx-react';
import noop from 'lodash/noop';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import warning from 'choerodon-ui/lib/_util/warning';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import { Tooltip as TextTooltip } from '../core/enum';
import autobind from '../_util/autobind';
import Validator from '../validator/Validator';
import Validity from '../validator/Validity';
import FormContext from '../form/FormContext';
import DataSetComponent from '../data-set/DataSetComponent';
import isEmpty from '../_util/isEmpty';
import { FieldTrim, FieldType } from '../data-set/enum';
import { ShowHelp } from './enum';
import { FIELD_SUFFIX } from '../form/utils';
import { LabelLayout, ShowValidation } from '../form/enum';
import Animate from '../animate';
import { defaultRenderer, fromRangeValue, getDateFormatByField, getValueKey, isFieldValueEmpty, processValue, renderMultipleValues, renderRangeValue, renderValidationMessage, showValidationMessage, toMultipleValue, toRangeValue, transformHighlightProps, } from './utils';
import isSame from '../_util/isSame';
import formatString from '../formatter/formatString';
import { hide, show } from '../tooltip/singleton';
import isOverflow from '../overflow-tip/util';
const map = {};
export function getFieldsById(id) {
    if (!map[id]) {
        map[id] = [];
    }
    return map[id];
}
export class FormField extends DataSetComponent {
    constructor() {
        super(...arguments);
        this.emptyValue = null;
        // 多选中出现了校验值的数量大于一那么输入框不需要存在校验信息展示
        this.multipleValidateMessageLength = 0;
    }
    static get contextType() {
        return FormContext;
    }
    get name() {
        return this.observableProps.name;
    }
    get value() {
        return this.observableProps.value;
    }
    set value(value) {
        runInAction(() => {
            this.observableProps.value = value;
        });
    }
    get labelLayout() {
        return this.props.labelLayout || this.context.labelLayout;
    }
    get labelTooltip() {
        return this.props.labelTooltip || this.context.labelTooltip || this.context.getTooltip('label');
    }
    get hasFloatLabel() {
        const { labelLayout } = this;
        return labelLayout === LabelLayout.float;
    }
    get isControlled() {
        return this.getControlled(this.props);
    }
    get pristine() {
        return this.observableProps.pristine;
    }
    get defaultValidationMessages() {
        return {};
    }
    get editable() {
        return this.isEditable();
    }
    get dataSet() {
        const { record } = this;
        if (record) {
            return record.dataSet;
        }
        const { observableProps } = this;
        if ('dataSet' in observableProps) {
            return observableProps.dataSet;
        }
        return observableProps.contextDataSet;
    }
    get record() {
        const { observableProps } = this;
        if ('record' in observableProps) {
            return observableProps.record;
        }
        const { dataSet, dataIndex } = observableProps;
        if (dataSet) {
            if (isNumber(dataIndex)) {
                return dataSet.get(dataIndex);
            }
            return dataSet.current;
        }
        if (isNumber(dataIndex)) {
            const { contextDataSet } = observableProps;
            if (contextDataSet) {
                return contextDataSet.get(dataIndex);
            }
        }
        return observableProps.contextRecord;
    }
    get dataIndex() {
        const { dataIndex, contextDataIndex } = this.observableProps;
        return defaultTo(dataIndex, contextDataIndex);
    }
    get field() {
        const { dataSet, name, observableProps } = this;
        const { displayName } = this.constructor;
        if (displayName !== 'Output' && !name) {
            warning(!observableProps.dataSet, `${displayName} with binding DataSet need property name.`);
            warning(!observableProps.record, `${displayName} with binding Record need property name.`);
        }
        if (name) {
            // const recordField = record ? record.getField(name) : undefined;
            // if (recordField) {
            //   return recordField;
            // }
            if (dataSet) {
                return dataSet.getField(name);
            }
        }
        return undefined;
    }
    get valid() {
        return this.isValid();
    }
    get multiple() {
        return this.getProp('multiple');
    }
    /**
     * 获取字段货币属性
     */
    get currency() {
        return this.getProp('currency');
    }
    get trim() {
        return this.getProp('trim');
    }
    get format() {
        return this.getProp('format');
    }
    get range() {
        const { field, observableProps, record } = this;
        if (field) {
            return field && field.get('range', record);
        }
        return 'range' in observableProps ? observableProps('range') : this.props.range;
    }
    get readOnly() {
        return this.isReadOnly();
    }
    get highlight() {
        return this.getDisplayProp('highlight');
    }
    get showValidation() {
        const { showValidation = this.getContextConfig('showValidation') } = this.observableProps;
        return showValidation;
    }
    get showHelp() {
        const { showHelp = this.getContextConfig('showHelp') } = this.observableProps;
        return showHelp;
    }
    get helpTooltipProps() {
        const { helpTooltipProps } = this.observableProps;
        return helpTooltipProps;
    }
    get highlightRenderer() {
        const { highlightRenderer = this.getContextConfig('highlightRenderer') } = this.observableProps;
        return highlightRenderer;
    }
    get rangeSeparator() {
        const rangeSeparator = this.getContextConfig('rangeSeparator');
        return rangeSeparator || '~';
    }
    getControlled(props) {
        return props.value !== undefined;
    }
    defaultRenderer(renderProps) {
        return defaultRenderer(renderProps);
    }
    /**
     * 判断是否应该显示验证信息, 作为属性传给Tooltip
     *
     * @readonly
     * @type {(undefined | boolean)}
     * @memberof FormField
     */
    isValidationMessageHidden(message) {
        const { hidden, noValidate } = this.props;
        return !!(hidden || !message || this.pristine || (!this.record && noValidate));
    }
    showValidationMessage(e, message) {
        showValidationMessage(e, message, this.context.getTooltipTheme('validation'), this.context.getTooltipPlacement('validation'), this.getContextConfig);
    }
    isEmpty() {
        const value = this.getValue();
        return isFieldValueEmpty(value, this.range, this.multiple);
    }
    isValid() {
        if (this.pristine) {
            return true;
        }
        const { field } = this;
        if (field) {
            return field.isValid(this.record);
        }
        const { validationResults } = this;
        if (validationResults) {
            return !validationResults.length;
        }
        return true;
    }
    isReadOnly() {
        const { readOnly } = this.observableProps;
        if (readOnly || this.pristine) {
            return true;
        }
        const { field } = this;
        return field ? field.get('readOnly', this.record) : false;
    }
    isEditable() {
        return !this.disabled && !this.readOnly;
    }
    isEditableLike() {
        return false;
    }
    getObservablePropsExcludeOutput(props, context) {
        return {
            readOnly: context.readOnly || props.readOnly || (this.getControlled(props) && !props.onChange && !props.onInput),
        };
    }
    getObservableProps(props, context) {
        const observableProps = {
            ...super.getObservableProps(props, context),
            ...this.getObservablePropsExcludeOutput(props, context),
            label: props.label,
            name: props.name,
            contextDataSet: context.dataSet,
            contextRecord: context.record,
            dataIndex: props.dataIndex,
            contextDataIndex: context.dataIndex,
            value: 'value' in props ? props.value : this.observableProps ? this.observableProps.value : props.defaultValue,
            disabled: context.disabled || props.disabled,
            pristine: context.pristine || props.pristine,
            highlight: props.highlight,
            highlightRenderer: 'highlightRenderer' in props ? props.highlightRenderer : context.fieldHighlightRenderer,
            showValidation: 'showValidation' in props ? props.showValidation : context.showValidation,
            showHelp: 'showHelp' in props ? props.showHelp : context.showHelp,
            helpTooltipProps: props.helpTooltipProps,
        };
        if ('record' in props) {
            observableProps.record = props.record;
        }
        return observableProps;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'record',
            'defaultValue',
            'dataIndex',
            'onEnterDown',
            'onClear',
            'onBeforeChange',
            'readOnly',
            'validator',
            'validationRenderer',
            'help',
            'showHelp',
            'helpTooltipProps',
            'renderer',
            'maxTagPlaceholder',
            'maxTagCount',
            'maxTagTextLength',
            'rowIndex',
            'colIndex',
            'labelLayout',
            '_inTable',
            'labelWidth',
            'pristine',
            'range',
            'trim',
            'newLine',
            'fieldClassName',
            'preventRenderer',
            'highlight',
            'highlightRenderer',
            'labelTooltip',
            'isFlat',
            'useColon',
            'requiredMarkAlign',
            'showValidation',
            'tagRenderer',
        ]);
    }
    getOtherPropsExcludeOutput(otherProps) {
        otherProps.onChange = !this.disabled && !this.readOnly ? this.handleChange : noop;
        otherProps.onKeyDown = this.handleKeyDown;
        otherProps.onCompositionStart = this.handleCompositionStart;
        otherProps.onCompositionEnd = this.handleChange;
        return otherProps;
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        otherProps.onMouseEnter = this.handleMouseEnter;
        otherProps.onMouseLeave = this.handleMouseLeave;
        return this.getOtherPropsExcludeOutput(otherProps);
    }
    getWrapperClassNamesExcludeOutput(prefixCls, empty) {
        return {
            [`${prefixCls}-empty`]: empty,
            [`${prefixCls}-readonly`]: this.readOnly,
        };
    }
    getWrapperClassNames(...args) {
        const { prefixCls } = this;
        const required = this.getProp('required');
        const empty = this.isEmpty();
        return super.getWrapperClassNames({
            [`${prefixCls}-highlight`]: this.highlight,
            [`${prefixCls}-invalid`]: !this.valid,
            [`${prefixCls}-float-label`]: this.hasFloatLabel,
            [`${prefixCls}-required`]: required,
            [`${prefixCls}-required-colors`]: required && (empty || !this.getContextConfig('showRequiredColorsOnlyEmpty')),
        }, this.getWrapperClassNamesExcludeOutput(prefixCls, empty), ...args);
    }
    renderWrapper() {
        return undefined;
    }
    renderHelpMessage() {
        const { showHelp } = this;
        if (showHelp === ShowHelp.newLine) {
            const help = this.getDisplayProp('help');
            if (help) {
                return (React.createElement("div", { key: "help", className: `${this.getContextProPrefixCls(FIELD_SUFFIX)}-help` }, help));
            }
        }
    }
    getLabel() {
        return toJS(this.getProp('label'));
    }
    renderFloatLabel() {
        if (this.hasFloatLabel) {
            const label = this.getLabel();
            if (label) {
                const { floatLabelOffsetX } = this;
                const prefixCls = this.getContextProPrefixCls(FIELD_SUFFIX);
                const required = this.getProp('required');
                const classString = classNames(`${prefixCls}-label`, {
                    [`${prefixCls}-required`]: required,
                    [`${prefixCls}-readonly`]: this.readOnly,
                });
                const style = floatLabelOffsetX ? {
                    marginLeft: pxToRem(floatLabelOffsetX, true),
                } : undefined;
                return (React.createElement("div", { className: `${prefixCls}-label-wrapper`, style: style },
                    React.createElement("div", { className: classString, onMouseEnter: this.handleFloatLabelMouseEnter, onMouseLeave: this.handleFloatLabelMouseLeave }, label)));
            }
        }
    }
    componentDidMount() {
        super.componentDidMount();
        this.addToForm(this.props, this.context);
    }
    componentWillReceiveProps(nextProps, nextContext) {
        super.componentWillReceiveProps(nextProps, nextContext);
        this.removeFromForm(this.props, this.context);
        this.addToForm(nextProps, nextContext);
        if (!this.record && this.props.value !== nextProps.value) {
            this.validate(nextProps.value);
        }
    }
    componentWillUnmount() {
        this.removeFromForm(this.props, this.context);
        if (this.tooltipShown) {
            hide();
            this.tooltipShown = false;
        }
    }
    addToForm(props, context) {
        const { form } = props;
        const { formNode } = context;
        if (form) {
            let fields = map[form];
            if (!fields) {
                fields = [];
                map[form] = fields;
            }
            fields.push(this);
        }
        else if (formNode) {
            formNode.addField(this);
        }
    }
    removeFromForm(props, context) {
        const { form } = props;
        const { formNode } = context;
        if (form) {
            const fields = map[form];
            if (fields) {
                const index = fields.indexOf(this);
                if (index !== -1) {
                    fields.splice(index, 1);
                }
            }
        }
        else if (formNode) {
            formNode.removeField(this);
        }
    }
    renderValidationResult(validationResult) {
        const validationMessage = this.getValidationMessage(validationResult);
        const { labelLayout, getProPrefixCls } = this.context;
        if (validationMessage) {
            const showIcon = !(labelLayout === LabelLayout.float || this.showValidation === ShowValidation.newLine);
            return renderValidationMessage(validationMessage, showIcon, getProPrefixCls);
        }
    }
    getValidatorProp(key) {
        switch (key) {
            case 'type':
                return this.getFieldType();
            case 'customValidator':
                return this.getProp('validator');
            case 'range':
                return this.range;
            case 'multiple':
                return this.multiple;
            case 'defaultValidationMessages':
                return {
                    ...this.defaultValidationMessages,
                    ...this.getContextConfig('defaultValidationMessages'),
                };
            default:
                return this.getProp(key);
        }
    }
    getValidatorProps() {
        const { name } = this;
        return {
            name,
            form: this.context.formNode,
        };
    }
    getValidationResults() {
        const { field, record } = this;
        if (field) {
            return field.getValidationErrorValues(record);
        }
        return this.validationResults;
    }
    getValidationMessage(validationResult) {
        if (validationResult === undefined) {
            const results = this.getValidationResults();
            if (results && results.length) {
                validationResult = results[0];
            }
        }
        const { props: { validationRenderer }, } = this;
        if (validationResult) {
            if (validationRenderer) {
                const validationMessage = validationRenderer(validationResult, validationResult.validationProps);
                if (validationMessage) {
                    return validationMessage;
                }
            }
            return validationResult.validationMessage;
        }
    }
    showTooltip(e) {
        if (!this.hasFloatLabel && this.showValidation === ShowValidation.tooltip) {
            const message = this.getTooltipValidationMessage();
            if (message) {
                showValidationMessage(e, message, this.context.getTooltipTheme('validation'), this.context.getTooltipPlacement('validation'), this.getContextConfig);
                return true;
            }
        }
        return false;
    }
    handleMouseEnter(e) {
        if (this.showTooltip(e)) {
            this.tooltipShown = true;
        }
        const { onMouseEnter = noop } = this.props;
        onMouseEnter(e);
    }
    handleMouseLeave(e) {
        if (this.tooltipShown) {
            hide();
            this.tooltipShown = false;
        }
        const { onMouseLeave = noop } = this.props;
        onMouseLeave(e);
    }
    handleFloatLabelMouseEnter(e) {
        const { labelTooltip, context: { getTooltipTheme, getTooltipPlacement } } = this;
        const { currentTarget } = e;
        if (labelTooltip === TextTooltip.always || (labelTooltip === TextTooltip.overflow && isOverflow(currentTarget))) {
            show(currentTarget, {
                title: this.getLabel(),
                placement: getTooltipPlacement('label'),
                theme: getTooltipTheme('label'),
            });
            this.tooltipShown = true;
        }
        else if (isArrayLike(labelTooltip)) {
            const tooltipType = labelTooltip[0];
            const labelTooltipProps = labelTooltip[1] || {};
            const duration = (labelTooltipProps.mouseEnterDelay || 0.1) * 1000;
            if (tooltipType === TextTooltip.always || (tooltipType === TextTooltip.overflow && isOverflow(currentTarget))) {
                show(currentTarget, {
                    theme: getTooltipTheme('label'),
                    placement: getTooltipPlacement('label'),
                    title: labelTooltipProps.title ? labelTooltipProps.title : this.getLabel(),
                    ...labelTooltipProps,
                }, duration);
                this.tooltipShown = true;
            }
        }
    }
    handleFloatLabelMouseLeave() {
        hide();
    }
    handleFocus(e) {
        super.handleFocus(e);
        if (this.range && !this.rangeValue) {
            this.beginRange();
        }
    }
    handleBlur(e) {
        super.handleBlur(e);
        if (this.range && (this.editable || this.isEditableLike())) {
            this.endRange();
        }
    }
    handleCompositionStart() {
        this.lock = true;
    }
    handleChange(e) {
        delete this.lock;
        e.preventDefault();
        e.stopPropagation();
    }
    handleKeyDown(e) {
        const { onKeyDown = noop, onEnterDown = noop } = this.props;
        onKeyDown(e);
        if (!e.isDefaultPrevented()) {
            switch (e.keyCode) {
                case KeyCode.ENTER:
                    this.handleEnterDown(e);
                    onEnterDown(e);
                    break;
                case KeyCode.ESC:
                    this.blur();
                    break;
                default:
            }
        }
    }
    handleEnterDown(e) {
        if (this.multiple) {
            if (this.range) {
                this.endRange();
                e.preventDefault();
            }
            else {
                const { value } = e.target;
                if (value !== '') {
                    this.syncValueOnBlur(value);
                    e.preventDefault();
                }
            }
        }
        else {
            this.blur();
        }
    }
    syncValueOnBlur(value) {
        this.prepareSetValue(value);
    }
    handleMutipleValueRemove(e, value, index) {
        this.removeValue(value, index);
        e.stopPropagation();
    }
    getDateFormat(field = this.field) {
        return getDateFormatByField(field, this.getFieldType(field), this.record);
    }
    getProcessValueOptions() {
        return {
            dateFormat: this.getDateFormat(),
            showInvalidDate: this.getContextConfig('showInvalidDate'),
        };
    }
    processValue(value) {
        return processValue(value, this.getProcessValueOptions());
    }
    getDataSetValue() {
        const { record, pristine, name } = this;
        if (record) {
            return pristine ? record.getPristineValue(name) : record.get(name);
        }
    }
    getTextNode(value = this.getValue()) {
        return this.getTextByValue(value);
    }
    getTextByValue(value) {
        const { preventRenderer } = this.props;
        const text = this.editable && (preventRenderer || this.isFocused)
            ? this.processValue(value)
            : this.processRenderer(value);
        return text;
    }
    getText(value) {
        return this.processValue(value);
    }
    processText(value) {
        return value;
    }
    processRenderer(value, repeat) {
        const { field, record, dataSet, props: { renderer = this.defaultRenderer, name, maxTagTextLength }, } = this;
        let processedValue;
        if (field && (field.getLookup(record) || field.get('options', record) || field.get('lovCode', record))) {
            // Cascader 值集处理
            if (isArrayLike(value)) {
                const isCascader = !field.get('multiple', record) || value.some(v => isArrayLike(v));
                processedValue = value.map(v => field.getText(v, undefined, record)).join(isCascader ? '/' : '、');
            }
            else {
                processedValue = field.getText(value, undefined, record);
            }
        }
        // 值集中不存在 再去取直接返回的值
        const text = this.processText(isNil(processedValue) ? this.processValue(value) : processedValue);
        return renderer
            ? renderer({
                value,
                text,
                record,
                dataSet,
                name,
                repeat,
                maxTagTextLength,
            })
            : text;
    }
    processRangeValue(value) {
        if (value === undefined && !this.multiple) {
            value = toRangeValue(this.getValue(), this.range);
        }
        return value || [];
    }
    getOldValue() {
        return this.getValue();
    }
    getValue() {
        const { name } = this;
        if (this.dataSet && name) {
            return this.getDataSetValue();
        }
        return this.value;
    }
    getValues() {
        return toMultipleValue(this.getValue(), this.range);
    }
    addValue(...values) {
        if (this.multiple) {
            const oldValues = this.getValues();
            if (values.length) {
                const { range } = this;
                this.setValue(uniqWith([...(isArrayLike(range) ? oldValues.map(v => fromRangeValue(v, range)) : oldValues), ...values], this.compare));
            }
            else if (!oldValues.length) {
                this.setValue(this.emptyValue);
            }
            else {
                this.validate(oldValues, false);
            }
        }
        else {
            this.setValue(values.pop());
        }
    }
    isLowerRange(_value1, _value2) {
        return false;
    }
    exchangeRangeValue(start, end) {
        const { rangeValue } = this;
        if (rangeValue) {
            rangeValue[0] = end;
            rangeValue[1] = start;
        }
    }
    prepareSetValue(...value) {
        const processV = this.getProp('processValue');
        const { range } = this;
        let values = value.filter(item => isNumber(item) || !isEmpty(item));
        if (range) {
            const { rangeTarget, rangeValue } = this;
            if (rangeTarget !== undefined && rangeValue) {
                const [start, end] = rangeValue;
                let newValue = values.pop();
                if (isFunction(processV)) {
                    newValue = processV(newValue, rangeTarget);
                }
                rangeValue[rangeTarget] = newValue;
                if (rangeTarget === 0 && (newValue || isNumber(newValue)) && (end || isNumber(end)) && this.isLowerRange(end, newValue)) {
                    this.exchangeRangeValue(newValue, end);
                }
                if (rangeTarget === 1 && (newValue || isNumber(newValue)) && (start || isNumber(start)) && this.isLowerRange(newValue, start)) {
                    this.exchangeRangeValue(start, newValue);
                }
            }
        }
        else {
            if (isFunction(processV)) {
                values = values.map(v => processV(v, undefined));
            }
            this.addValue(...values);
        }
    }
    removeValues(values, index = 0) {
        let repeat;
        const newValues = values.reduce((oldValues, value) => {
            repeat = 0;
            const valueKey = this.getValueKey(value);
            return oldValues.filter(v => {
                if (this.getValueKey(v) === valueKey) {
                    if (index === -1 || repeat === index) {
                        this.afterRemoveValue(value, repeat++);
                        return false;
                    }
                    repeat++;
                }
                return true;
            });
        }, this.getValues());
        const { range } = this;
        this.setValue(isArrayLike(range) ? newValues.map(v => fromRangeValue(v, range)) : newValues);
    }
    removeValue(value, index = 0) {
        this.removeValues([value], index);
    }
    removeLastValue() {
        const values = this.getValues();
        const index = findLastIndex(values, (value) => !this.isMultipleBlockDisabled(value));
        if (index !== -1) {
            const [value] = values.splice(index, 1);
            const { range } = this;
            this.setValue(isArrayLike(range) ? values.map(v => fromRangeValue(v, range)) : values);
            this.afterRemoveValue(fromRangeValue(value, range), -1);
            return value;
        }
    }
    afterRemoveValue(_value, _repeat) {
        // noop
    }
    beginRange() {
        this.setRangeTarget(this.rangeTarget || 0);
        this.rangeValue = this.multiple
            ? [undefined, undefined]
            : toRangeValue(this.getValue(), this.range);
    }
    endRange() {
        if (this.rangeValue) {
            const values = this.rangeValue.slice();
            this.rangeValue = undefined;
            if (!this.multiple || !values.every(isNil)) {
                this.addValue(fromRangeValue(values, this.range));
            }
        }
        this.setRangeTarget(undefined);
    }
    setRangeTarget(target) {
        this.rangeTarget = target;
    }
    compare(oldValue, newValue) {
        return isSame(toJS(oldValue), toJS(newValue));
    }
    autoCreate() {
        const { record } = this;
        if (record) {
            return record;
        }
        const { dataSet, name, dataIndex } = this;
        if (dataSet && name) {
            return dataSet.create({}, dataIndex);
        }
    }
    setValue(value, noVaidate) {
        if (!this.readOnly) {
            if (this.multiple || this.range
                ? isArrayLike(value) && !value.length
                : isNil(value) || value === '') {
                value = this.emptyValue;
            }
            const { name, dataSet, trim, format, } = this;
            const { onChange = noop, onBeforeChange = noop } = this.props;
            const old = toJS(this.getOldValue());
            if (!dataSet || !name) {
                value = formatString(value, {
                    trim,
                    format,
                });
            }
            const updateAndValidate = () => {
                if (dataSet && name) {
                    const record = this.autoCreate();
                    if (record) {
                        const { field } = this;
                        if (field && !field.get('defaultValidationMessages', record)) {
                            field.set('defaultValidationMessages', { ...this.defaultValidationMessages, label: this.getProp('label') });
                        }
                        record.set(name, value);
                    }
                }
                else if (!noVaidate) {
                    this.validate(value, false);
                }
            };
            // 转成实际的数据再进行判断
            if (!this.compare(old, toJS(value))) {
                const { formNode } = this.context;
                const storedValue = this.value;
                const beforeChange = onBeforeChange(value, old, formNode);
                const resolveCallback = action(() => {
                    updateAndValidate();
                    onChange(value, old, formNode);
                    this.afterSetValue();
                });
                if (isPromise(beforeChange)) {
                    this.value = value;
                    const rejectCallback = () => {
                        this.value = storedValue;
                    };
                    beforeChange.then(result => result === false ? rejectCallback() : resolveCallback());
                }
                else if (beforeChange !== false) {
                    resolveCallback();
                    this.value = value;
                }
            }
            else {
                updateAndValidate();
            }
        }
    }
    afterSetValue() {
        // noop
    }
    renderRangeValue(value, repeat) {
        return renderRangeValue(value, { repeat, processRenderer: this.processRenderer, rangeSeparator: this.rangeSeparator });
    }
    getValueKey(v) {
        return getValueKey(v);
    }
    isMultipleBlockDisabled(_v) {
        return this.disabled;
    }
    renderMultipleValues(readOnly) {
        const { prefixCls, range, disabled, props: { maxTagCount = this.getContextConfig('fieldMaxTagCount'), maxTagPlaceholder = this.getContextConfig('fieldMaxTagPlaceholder'), tagRenderer, }, } = this;
        const values = renderMultipleValues(this.getValue(), {
            range,
            maxTagCount,
            maxTagPlaceholder,
            prefixCls,
            disabled,
            tagRenderer,
            readOnly: this.readOnly || readOnly,
            validationResults: this.getValidationResults(),
            isMultipleBlockDisabled: this.isMultipleBlockDisabled,
            processRenderer: this.processRenderer,
            renderValidationResult: this.renderValidationResult,
            handleMultipleValueRemove: this.handleMutipleValueRemove,
            isValidationMessageHidden: this.isValidationMessageHidden,
            showValidationMessage: this.showValidationMessage,
            getKey: this.getValueKey,
            rangeSeparator: this.rangeSeparator,
        });
        this.multipleValidateMessageLength = values.multipleValidateMessageLength;
        return values;
    }
    clear() {
        const { onClear = noop } = this.props;
        this.setValue(this.emptyValue);
        this.rangeValue = this.isFocused ? [undefined, undefined] : undefined;
        onClear();
    }
    async checkValidity() {
        const { name } = this;
        const valid = await this.validate();
        const { onInvalid = noop } = this.props;
        if (!valid) {
            const validationResults = this.getValidationResults();
            if (validationResults) {
                const currentValidationResult = validationResults[0];
                onInvalid(validationResults, new Validity(currentValidationResult ? { [currentValidationResult.ruleName]: true } : undefined), name);
            }
        }
        return valid;
    }
    validate(value, report = true) {
        if (!this.props.noValidate) {
            if (value === undefined) {
                value = this.multiple ? this.getValues() : this.getValue();
            }
            const { field, record, name, dataSet } = this;
            return Validator.checkValidity(value, field && record ? {
                dataSet,
                record,
                name,
            } : this.getValidatorProps(), field && record ? field.getValidatorPropGetter(record) : this.getValidatorProp.bind(this)).then(action(({ valid, validationResults, validatorProps }) => {
                if (record && name) {
                    record.setValidationError(name, validationResults);
                }
                else {
                    this.validationResults = validationResults;
                }
                if (report) {
                    Validator.report(validationResults, validatorProps);
                    const { formNode } = this.context;
                    if (formNode && !formNode.validating) {
                        formNode.reportValidity(valid);
                    }
                }
                return valid;
            }));
        }
        return Promise.resolve(true);
    }
    isDisabled() {
        if (super.isDisabled()) {
            return true;
        }
        const { field, record } = this;
        if (field) {
            const disabled = field.get('disabled', record);
            if (disabled) {
                return true;
            }
            const cascadeMap = field.get('cascadeMap', record);
            if (cascadeMap &&
                (!record || Object.keys(cascadeMap).some(cascade => {
                    if (isObject(record.get(cascadeMap[cascade]))) {
                        return isLdEmpty(record.get(cascadeMap[cascade]));
                    }
                    return isNil(record.get(cascadeMap[cascade]));
                }))) {
                return true;
            }
        }
        return false;
    }
    reset() {
        if (!this.isControlled && !this.dataSet) {
            this.setValue(this.props.defaultValue, true);
        }
        const { record, name } = this;
        if (record && name) {
            record.clearValidationError(name);
        }
        else {
            this.validationResults = undefined;
        }
    }
    getFieldType(field = this.field) {
        return (field && field.get('type', this.record)) || FieldType.string;
    }
    getProp(propName, fieldPropName = propName) {
        const { field, observableProps, record } = this;
        return defaultTo(field && field.get(fieldPropName, record), propName in observableProps ? observableProps[propName] : this.props[propName]);
    }
    /**
     * 显示类属性值获取（组件属性优先级高于 DataSet Field 属性优先级）
     * @param propName 组件属性名
     * @param fieldPropName Field 字段属性名
     * @returns
     */
    getDisplayProp(propName, fieldPropName = propName) {
        const { field, observableProps, record } = this;
        return defaultTo(propName in observableProps ? observableProps[propName] : this.props[propName], field && field.get(fieldPropName, record));
    }
    getTooltipValidationMessage() {
        const { _inTable } = this.props;
        if (!_inTable && !(!!(this.multiple && this.getValues().length) && !this.getProp('validator') || this.multipleValidateMessageLength > 0)) {
            const validationMessage = this.renderValidationResult();
            if (!this.isValidationMessageHidden(validationMessage)) {
                return validationMessage;
            }
        }
    }
    renderHighLight() {
        const wrapper = this.renderWrapper();
        const { highlight, dataSet, record, name } = this;
        if (highlight) {
            const hidden = !(this.hasFloatLabel || this.showValidation === ShowValidation.newLine || this.valid);
            const highlightWrapper = this.highlightRenderer(transformHighlightProps(highlight, { dataSet, record, name, hidden }), wrapper);
            if (isValidElement(highlightWrapper)) {
                return highlightWrapper;
            }
        }
        return wrapper;
    }
    render() {
        const wrapper = this.renderHighLight();
        const help = this.renderHelpMessage();
        if (this.hasFloatLabel || this.showValidation === ShowValidation.newLine) {
            const message = this.renderValidationResult();
            return [
                isValidElement(wrapper) ? cloneElement(wrapper, { key: 'wrapper' }) : wrapper,
                message && (React.createElement(Animate, { transitionName: "show-error", component: "", transitionAppear: true, key: "validation-message" }, message)),
                help,
            ];
        }
        return (React.createElement(React.Fragment, null,
            wrapper,
            help));
    }
    forcePositionChanged() {
        // noop
    }
}
FormField.defaultProps = {
    readOnly: false,
    disabled: false,
    noValidate: false,
    trim: FieldTrim.both,
};
__decorate([
    observable
], FormField.prototype, "floatLabelOffsetX", void 0);
__decorate([
    observable
], FormField.prototype, "rangeTarget", void 0);
__decorate([
    observable
], FormField.prototype, "rangeValue", void 0);
__decorate([
    observable
], FormField.prototype, "$validator", void 0);
__decorate([
    observable
], FormField.prototype, "validationResults", void 0);
__decorate([
    computed
], FormField.prototype, "editable", null);
__decorate([
    computed
], FormField.prototype, "dataSet", null);
__decorate([
    computed
], FormField.prototype, "record", null);
__decorate([
    computed
], FormField.prototype, "dataIndex", null);
__decorate([
    computed
], FormField.prototype, "field", null);
__decorate([
    computed
], FormField.prototype, "valid", null);
__decorate([
    computed
], FormField.prototype, "readOnly", null);
__decorate([
    computed
], FormField.prototype, "showValidation", null);
__decorate([
    computed
], FormField.prototype, "showHelp", null);
__decorate([
    computed
], FormField.prototype, "highlightRenderer", null);
__decorate([
    autobind
], FormField.prototype, "defaultRenderer", null);
__decorate([
    autobind
], FormField.prototype, "isValidationMessageHidden", null);
__decorate([
    autobind
], FormField.prototype, "showValidationMessage", null);
__decorate([
    autobind
], FormField.prototype, "renderValidationResult", null);
__decorate([
    autobind
], FormField.prototype, "handleMouseEnter", null);
__decorate([
    autobind
], FormField.prototype, "handleMouseLeave", null);
__decorate([
    autobind
], FormField.prototype, "handleFloatLabelMouseEnter", null);
__decorate([
    autobind
], FormField.prototype, "handleFocus", null);
__decorate([
    autobind
], FormField.prototype, "handleBlur", null);
__decorate([
    autobind
], FormField.prototype, "handleCompositionStart", null);
__decorate([
    autobind
], FormField.prototype, "handleChange", null);
__decorate([
    autobind
], FormField.prototype, "handleKeyDown", null);
__decorate([
    autobind
], FormField.prototype, "handleMutipleValueRemove", null);
__decorate([
    autobind
], FormField.prototype, "processRenderer", null);
__decorate([
    action
], FormField.prototype, "prepareSetValue", null);
__decorate([
    action
], FormField.prototype, "removeValues", null);
__decorate([
    action
], FormField.prototype, "removeLastValue", null);
__decorate([
    action
], FormField.prototype, "beginRange", null);
__decorate([
    action
], FormField.prototype, "endRange", null);
__decorate([
    action
], FormField.prototype, "setRangeTarget", null);
__decorate([
    autobind
], FormField.prototype, "compare", null);
__decorate([
    action
], FormField.prototype, "setValue", null);
__decorate([
    autobind
], FormField.prototype, "isMultipleBlockDisabled", null);
__decorate([
    action
], FormField.prototype, "clear", null);
__decorate([
    action
], FormField.prototype, "validate", null);
__decorate([
    autobind,
    action
], FormField.prototype, "reset", null);
__decorate([
    autobind
], FormField.prototype, "getTooltipValidationMessage", null);
let ObserverFormField = class ObserverFormField extends FormField {
};
ObserverFormField.defaultProps = FormField.defaultProps;
ObserverFormField = __decorate([
    observer
], ObserverFormField);
export default ObserverFormField;
